import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { registerMember, checkIdDuplicate, checkNicknameDuplicate } from '@/api/memberApi';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Form state
  const [memberId, setMemberId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [nickname, setNickname] = useState('');

  // Validation state
  const [idChecked, setIdChecked] = useState(false);
  const [nicknameChecked, setNicknameChecked] = useState(false);
  const [idAvailable, setIdAvailable] = useState(false);
  const [nicknameAvailable, setNicknameAvailable] = useState(false);

  // Error state
  const [errors, setErrors] = useState<{
    memberId?: string;
    password?: string;
    passwordConfirm?: string;
    nickname?: string;
  }>({});

  // ID 중복 체크
  const handleCheckIdDuplicate = async () => {
    if (!memberId.trim()) {
      setErrors({ ...errors, memberId: 'ID를 입력해주세요.' });
      return;
    }

    if (memberId.length < 4 || memberId.length > 20) {
      setErrors({ ...errors, memberId: 'ID는 4~20자로 입력해주세요.' });
      return;
    }

    try {
      const isAvailable = await checkIdDuplicate(memberId);
      setIdChecked(true);
      setIdAvailable(isAvailable);

      if (isAvailable) {
        toast({
          title: '사용 가능한 ID입니다.',
          variant: 'default',
        });
        setErrors({ ...errors, memberId: undefined });
      } else {
        toast({
          title: '이미 사용 중인 ID입니다.',
          variant: 'destructive',
        });
        setErrors({ ...errors, memberId: '이미 사용 중인 ID입니다.' });
      }
    } catch (error) {
      toast({
        title: 'ID 중복 체크 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    }
  };

  // 닉네임 중복 체크
  const handleCheckNicknameDuplicate = async () => {
    if (!nickname.trim()) {
      setErrors({ ...errors, nickname: '닉네임을 입력해주세요.' });
      return;
    }

    if (nickname.length < 2 || nickname.length > 20) {
      setErrors({ ...errors, nickname: '닉네임은 2~20자로 입력해주세요.' });
      return;
    }

    try {
      const isAvailable = await checkNicknameDuplicate(nickname);
      setNicknameChecked(true);
      setNicknameAvailable(isAvailable);

      if (isAvailable) {
        toast({
          title: '사용 가능한 닉네임입니다.',
          variant: 'default',
        });
        setErrors({ ...errors, nickname: undefined });
      } else {
        toast({
          title: '이미 사용 중인 닉네임입니다.',
          variant: 'destructive',
        });
        setErrors({ ...errors, nickname: '이미 사용 중인 닉네임입니다.' });
      }
    } catch (error) {
      toast({
        title: '닉네임 중복 체크 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    }
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!memberId.trim()) {
      newErrors.memberId = 'ID를 입력해주세요.';
    } else if (!idChecked || !idAvailable) {
      newErrors.memberId = 'ID 중복 체크를 해주세요.';
    }

    if (!password.trim()) {
      newErrors.password = '비밀번호를 입력해주세요.';
    } else if (password.length < 4) {
      newErrors.password = '비밀번호는 최소 4자 이상이어야 합니다.';
    }

    if (!passwordConfirm.trim()) {
      newErrors.passwordConfirm = '비밀번호 확인을 입력해주세요.';
    } else if (password !== passwordConfirm) {
      newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
    }

    if (!nickname.trim()) {
      newErrors.nickname = '닉네임을 입력해주세요.';
    } else if (!nicknameChecked || !nicknameAvailable) {
      newErrors.nickname = '닉네임 중복 체크를 해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await registerMember({
        memberId,
        password,
        nickname,
      });

      if (response.success) {
        toast({
          title: '회원가입 성공!',
          description: response.message,
        });

        // 승인 대기 페이지로 이동
        navigate('/wait', {
          state: {
            memberId: response.data.memberId,
            nickname: response.data.nickname
          }
        });
      } else {
        toast({
          title: '회원가입 실패',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '회원가입 중 오류가 발생했습니다.';
      toast({
        title: '오류',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // ID 입력 시 중복 체크 초기화
  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberId(e.target.value);
    setIdChecked(false);
    setIdAvailable(false);
  };

  // 닉네임 입력 시 중복 체크 초기화
  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNickname(e.target.value);
    setNicknameChecked(false);
    setNicknameAvailable(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">회원가입</CardTitle>
          <CardDescription className="text-center">
            ELLOAS에 오신 것을 환영합니다
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* ID 입력 */}
            <div className="space-y-2">
              <Label htmlFor="memberId">
                아이디 <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="memberId"
                  type="text"
                  placeholder="4~20자"
                  value={memberId}
                  onChange={handleIdChange}
                  className={errors.memberId ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckIdDuplicate}
                  disabled={!memberId.trim()}
                >
                  중복확인
                </Button>
              </div>
              {errors.memberId && (
                <p className="text-sm text-red-500">{errors.memberId}</p>
              )}
              {idChecked && idAvailable && (
                <p className="text-sm text-green-600">사용 가능한 ID입니다.</p>
              )}
            </div>

            {/* 비밀번호 입력 */}
            <div className="space-y-2">
              <Label htmlFor="password">
                비밀번호 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="최소 4자 이상"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password}</p>
              )}
            </div>

            {/* 비밀번호 확인 */}
            <div className="space-y-2">
              <Label htmlFor="passwordConfirm">
                비밀번호 확인 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="passwordConfirm"
                type="password"
                placeholder="비밀번호 재입력"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={errors.passwordConfirm ? 'border-red-500' : ''}
              />
              {errors.passwordConfirm && (
                <p className="text-sm text-red-500">{errors.passwordConfirm}</p>
              )}
              {password && passwordConfirm && password === passwordConfirm && (
                <p className="text-sm text-green-600">비밀번호가 일치합니다.</p>
              )}
            </div>

            {/* 닉네임 입력 */}
            <div className="space-y-2">
              <Label htmlFor="nickname">
                닉네임 <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="nickname"
                  type="text"
                  placeholder="2~20자"
                  value={nickname}
                  onChange={handleNicknameChange}
                  className={errors.nickname ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckNicknameDuplicate}
                  disabled={!nickname.trim()}
                >
                  중복확인
                </Button>
              </div>
              {errors.nickname && (
                <p className="text-sm text-red-500">{errors.nickname}</p>
              )}
              {nicknameChecked && nicknameAvailable && (
                <p className="text-sm text-green-600">사용 가능한 닉네임입니다.</p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-3">
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? '가입 중...' : '회원가입'}
            </Button>

            <div className="text-sm text-center text-muted-foreground">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="text-primary hover:underline">
                로그인
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
